import { create } from 'zustand';
import { practiceTypeService, PracticeType } from '@/services/practiceTypeService';

interface PracticeTypeState {
  practiceTypes: PracticeType[];
  currentType: PracticeType | null;
  loading: boolean;
  error: string | null;
  
  // Actions
  fetchPracticeTypes: (activeOnly?: boolean) => Promise<void>;
  fetchPracticeType: (id: string) => Promise<void>;
  createPracticeType: (data: Partial<PracticeType>) => Promise<PracticeType>;
  updatePracticeType: (id: string, data: Partial<PracticeType>) => Promise<void>;
  deletePracticeType: (id: string) => Promise<void>;
  reorderPracticeTypes: (order: { id: string; order: number }[]) => Promise<void>;
  reset: () => void;
}

const initialState = {
  practiceTypes: [],
  currentType: null,
  loading: false,
  error: null,
};

export const usePracticeTypeStore = create<PracticeTypeState>((set, get) => ({
  ...initialState,

  fetchPracticeTypes: async (activeOnly = true) => {
    set({ loading: true, error: null });
    try {
      const response = await practiceTypeService.getPracticeTypes(activeOnly);
      set({
        practiceTypes: response.data || [],
        loading: false,
      });
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Errore nel caricamento dei tipi pratiche',
        loading: false,
      });
    }
  },

  fetchPracticeType: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const response = await practiceTypeService.getPracticeType(id);
      set({
        currentType: response.data,
        loading: false,
      });
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Errore nel caricamento del tipo pratica',
        loading: false,
      });
    }
  },

  createPracticeType: async (data: Partial<PracticeType>) => {
    set({ loading: true, error: null });
    try {
      const response = await practiceTypeService.createPracticeType(data);
      const newType = response.data;
      set((state) => ({
        practiceTypes: [...state.practiceTypes, newType].sort((a, b) => a.order - b.order),
        loading: false,
      }));
      return newType;
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Errore nella creazione del tipo pratica';
      set({ error: errorMsg, loading: false });
      throw error;
    }
  },

  updatePracticeType: async (id: string, data: Partial<PracticeType>) => {
    set({ loading: true, error: null });
    try {
      const response = await practiceTypeService.updatePracticeType(id, data);
      const updatedType = response.data;
      set((state) => ({
        practiceTypes: state.practiceTypes.map((t) => (t.id === id ? updatedType : t)).sort((a, b) => a.order - b.order),
        currentType: state.currentType?.id === id ? updatedType : state.currentType,
        loading: false,
      }));
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Errore nell\'aggiornamento del tipo pratica',
        loading: false,
      });
      throw error;
    }
  },

  deletePracticeType: async (id: string) => {
    set({ loading: true, error: null });
    try {
      await practiceTypeService.deletePracticeType(id);
      set((state) => ({
        practiceTypes: state.practiceTypes.filter((t) => t.id !== id),
        currentType: state.currentType?.id === id ? null : state.currentType,
        loading: false,
      }));
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Errore nell\'eliminazione del tipo pratica',
        loading: false,
      });
      throw error;
    }
  },

  reorderPracticeTypes: async (order: { id: string; order: number }[]) => {
    set({ loading: true, error: null });
    try {
      await practiceTypeService.reorderPracticeTypes(order);
      // Ricarica i tipi per avere l'ordine aggiornato
      await get().fetchPracticeTypes();
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Errore nel riordinamento dei tipi pratiche',
        loading: false,
      });
      throw error;
    }
  },

  reset: () => {
    set(initialState);
  },
}));

